import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery, { getToken, ResponseData } from '../root';
import { ChatbotRTKService } from './chatbot-rtk.services';
import { IPackage, ISearchPackageParams } from './package-rtk.type';

const baseUrl = `${import.meta.env.VITE_API_DOMAIN ? import.meta.env.VITE_API_DOMAIN : '/api'}`;

const baseQuery = axiosBaseQuery({
  baseUrl,
});

export const PackageRTKService = createApi({
  reducerPath: 'PackageRTKService',
  baseQuery,
  tagTypes: [
    'GetPackages',
    'GetPackageDetail',
    'GetPackagesVersionList',
    'GetTransaction',
    'GetTransactionsByPackageId',
    'GetProfilePayout',
    'GetPayoutNotPayed',
    'GetTotalByUser',
  ],
  endpoints: builder => ({
    createPackage: builder.mutation<any, IPackage>({
      query: ({ type, ...rest }) => ({
        url: `/packages/${type}`,
        method: 'POST',
        data: rest,
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': getToken(),
        },
      }),
      invalidatesTags: () => ['GetPackages'],
      transformResponse: response => response.element,
    }),
    getUserPackages: builder.query<any, void>({
      query: () => ({
        url: `/packages`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      providesTags: () => ['GetPackages'],
      transformResponse: (response: any) => response.element,
    }),
    getPackageDetail: builder.query<any, string>({
      query: package_id => ({
        url: `/packages/${package_id}`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      providesTags: (result, error, package_id) => [{ type: 'GetPackageDetail', package_id }],
      transformResponse: (response: any) => response.element,
    }),
    marketplacePayment: builder.mutation<
      any,
      {
        redirectUrl: string;
        amount: number;
        currency: string;
        productId: string;
      }
    >({
      query: ({ redirectUrl, amount, currency, productId }) => ({
        url: `/billing/marketplace-payment?redirectUrl=${redirectUrl}&amount=${amount}&currency=${currency}&provider=marketplace&productId=${productId}`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      transformResponse: (responseData: ResponseData) => responseData?.element?.redirect,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(ChatbotRTKService.util.invalidateTags(['Package', 'GetAllScenarios']));
        } catch {
          // Handle error silently
        }
      },
    }),
    getPackagesByName: builder.query<any, ISearchPackageParams>({
      query: ({ query, limit, page }) => ({
        url: `/packages/get/by-name?name=${query}&limit=${limit}&page=${page}`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      providesTags: ['GetPackagesVersionList'],
      transformResponse: (response: any) => response.element,
    }),
    updatePackageStatus: builder.mutation<any, { package_id: string; status: string }>({
      query: ({ package_id, status }: { package_id: string; status: string }) => ({
        url: `/packages/${package_id}`,
        method: 'PATCH',
        data: {
          status: status,
        },
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      invalidatesTags: ['GetPackagesVersionList', 'GetPackages'],
    }),
    getTransaction: builder.query<any, string>({
      query: transactionId => ({
        url: `/packages/transactions/${transactionId}`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      providesTags: ['GetTransaction'],
      transformResponse: (response: any) => response.element,
    }),
    addMessage: builder.mutation<any, { transactionId: string; message: string }>({
      query: ({ transactionId, message }) => ({
        url: `/packages/transactions/${transactionId}/message`,
        method: 'POST',
        data: { message: message },
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': getToken(),
        },
      }),
      invalidatesTags: () => ['GetTransaction'],
      transformResponse: response => response.element,
    }),
    updateUserStatus: builder.mutation<any, { transaction_id: string; status: string }>({
      query: ({ transaction_id, status }: { transaction_id: string; status: string }) => ({
        url: `/packages/transactions/${transaction_id}/user-status`,
        method: 'PATCH',
        data: {
          status: status,
        },
        headers: {
          'x-auth-token': getToken(),
        },
      }),
    }),
    disputeTransaction: builder.mutation<any, string>({
      query: transaction_id => ({
        url: `/packages/transactions/${transaction_id}/dispute`,
        method: 'PATCH',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
    }),
    getTransactionsByPackageId: builder.query<any, { package_id: string; limit: string; page: string }>({
      query: ({ package_id, limit, page }: { package_id: string; limit: string; page: string }) => ({
        url: `/packages/transactions/package/${package_id}?limit=${limit}&page=${page}`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      providesTags: ['GetTransactionsByPackageId'],
      transformResponse: (response: any) => response.element,
    }),
    updatePackage: builder.mutation<any, { body: IPackage; package_id: string }>({
      query: ({ body: body, package_id: package_id }) => ({
        url: `/packages/${package_id}`,
        method: 'PUT',
        data: body,
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': getToken(),
        },
      }),
      invalidatesTags: (result, error, { package_id }) => ['GetPackages', 'GetPackagesVersionList', { type: 'GetPackageDetail', package_id }],
      transformResponse: response => response.element,
    }),
    updateProfilePayout: builder.mutation<any, any>({
      query: data => ({
        url: `/packages/payout-info/update`,
        method: 'PATCH',
        headers: {
          'x-auth-token': getToken(),
        },
        data: data,
      }),
      transformResponse: (response: any) => response.element,
      invalidatesTags: ['GetProfilePayout'],
    }),
    getPayoutFetch: builder.query<any, void>({
      query: () => ({
        url: `/packages/payout-info/fetch`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      providesTags: ['GetProfilePayout'],
      transformResponse: (response: any) => response.element,
    }),
    getPayoutNotPayed: builder.query<any, void>({
      query: () => ({
        url: `/packages/billing/transactions/not-payed`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      providesTags: ['GetPayoutNotPayed'],
      transformResponse: (response: any) => response.element,
    }),
    collect: builder.mutation<any, void>({
      query: data => ({
        url: `/packages/billing/transactions/collect`,
        method: 'POST',
        headers: {
          'x-auth-token': getToken(),
        },
        data: data,
      }),
      transformResponse: (response: any) => response.element,
      invalidatesTags: ['GetTotalByUser', 'GetPayoutNotPayed'],
    }),
    getTotalByUser: builder.query<any, { page: string; limit: string }>({
      query: ({ limit, page }) => ({
        url: `/packages/billing/transactions/total-by-user?page=${page}&limit=${limit}`,
        method: 'GET',
        headers: {
          'x-auth-token': getToken(),
        },
      }),
      providesTags: ['GetTotalByUser'],
      transformResponse: (response: any) => response.element,
    }),
  }),
});

export const {
  useCreatePackageMutation,
  useGetUserPackagesQuery,
  useGetPackageDetailQuery,
  useLazyGetPackageDetailQuery,
  useMarketplacePaymentMutation,
  useGetPackagesByNameQuery,
  useUpdatePackageStatusMutation,
  useGetTransactionQuery,
  useAddMessageMutation,
  useUpdateUserStatusMutation,
  useDisputeTransactionMutation,
  useGetTransactionsByPackageIdQuery,
  useUpdatePackageMutation,
  useLazyGetPackagesByNameQuery,
  useUpdateProfilePayoutMutation,
  useGetPayoutFetchQuery,
  useGetPayoutNotPayedQuery,
  useCollectMutation,
  useGetTotalByUserQuery,
} = PackageRTKService;
